import React from "react";
import { Box } from "@mui/material";
import { GpsFixed as GpsFixedIcon } from "@mui/icons-material";
import { dtGreen } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { TrackingSession, TrackingPosition } from "../../../../shared/tracking/trackingSlice";
import ActiveServiceCard from "../ActiveServiceCard";
import LivePulseDot from "../LivePulseDot";

interface DesktopTrackingLayoutProps {
  activeSessions: TrackingSession[];
  positions: Record<string, TrackingPosition>;
  selectedSessionId: string | null;
  onSessionClick: (session: TrackingSession) => void;
  mapElement: React.ReactNode;
  connectionBanner: React.ReactNode;
}

const DesktopTrackingLayout: React.FC<DesktopTrackingLayoutProps> = ({
  activeSessions,
  positions,
  selectedSessionId,
  onSessionClick,
  mapElement,
  connectionBanner,
}) => {
  const { translate } = useTranslation(["serviceTracking"]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "calc(100vh - 120px)",
        backgroundColor: dtGreen.pageBg,
        overflow: "hidden",
      }}
    >
      {/* Left sidebar: 320px */}
      <Box
        sx={{
          width: 320,
          minWidth: 320,
          backgroundColor: dtGreen.cardBg,
          borderRight: `1px solid ${dtGreen.cardBorder}`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Sidebar header */}
        <Box
          sx={{
            p: "16px",
            borderBottom: `1px solid ${dtGreen.cardBorder}`,
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexShrink: 0,
          }}
        >
          <GpsFixedIcon sx={{ fontSize: 20, color: dtGreen.green500 }} />
          <Box
            sx={{
              fontFamily: dtGreen.font,
              fontSize: "15px",
              fontWeight: 600,
              color: dtGreen.textPrimary,
            }}
          >
            {translate("serviceTracking:title")}
          </Box>
          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <LivePulseDot size={6} color={dtGreen.green500} />
            <Box
              sx={{
                fontFamily: dtGreen.fontMono,
                fontSize: "12px",
                fontWeight: 600,
                color: dtGreen.green600,
              }}
            >
              {activeSessions.length}
            </Box>
          </Box>
        </Box>

        {/* Connection banner */}
        <Box sx={{ px: "12px", pt: "8px" }}>
          {connectionBanner}
        </Box>

        {/* Scrollable card list */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: "12px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: dtGreen.textMuted,
              borderRadius: 2,
            },
          }}
        >
          {activeSessions.map((session) => (
            <ActiveServiceCard
              key={session.id}
              session={session}
              position={positions[session.id] || null}
              isSelected={session.id === selectedSessionId}
              onClick={onSessionClick}
            />
          ))}
        </Box>
      </Box>

      {/* Right: full-height map */}
      <Box sx={{ flex: 1, height: "100%" }}>
        {mapElement}
      </Box>
    </Box>
  );
};

export default React.memo(DesktopTrackingLayout);
